/**
 * skinningIntegrity — geometric "is the avatar still in one piece?" probe.
 *
 * WHY THIS EXISTS
 * ----------------
 * The Avatar Animation Tester used to colour its coverage matrix GREEN
 * purely from parsing the SDK's `🦴 Gesture '<g>' — animating N role(s)
 * across M bone instance(s)` debug line. That line only proves the
 * gesture system *found bones and rotated them* — it says NOTHING about
 * whether the skinned meshes stay stitched together while those bones
 * rotate.
 *
 * That blind spot cuts both ways. The matrix can show "✓ 3/3" while the
 * head pulls off the neck (a real detach the role-count can't see); but a
 * naive geometric probe can also cry "TEAR" on a perfectly clean lean if it
 * measures the wrong (rotation-variant) quantity. Both failure modes are
 * expensive: the first ships a broken gesture, the second wrongly disables a
 * working one. This module is built to avoid BOTH by measuring only
 * rotation+translation-INVARIANT quantities (see the two signals below), so
 * its verdict reflects genuine skinning failure rather than the legitimate
 * rigid motion of the gesture itself.
 *
 * This module measures the thing that actually matters: do the skinned
 * meshes that make up the avatar move *rigidly together* while a gesture
 * plays, or do they tear / detach / explode?
 *
 * THE TWO SIGNALS (both rotation-invariant on purpose)
 * ----------------------------------------------------
 *  1. Inter-mesh TRUE-centroid-distance change ("seam separation").
 *     A lean is, to first order, a rigid rotation of the upper body.
 *     A rigid rotation PRESERVES the distance between any two mesh
 *     centroids (the MEAN of their vertices). So if the head mesh and the
 *     torso mesh rotate together correctly, |headCentroid − torsoCentroid|
 *     is the same at rest and at gesture peak. If the head detaches, that
 *     distance changes. We normalise the change by the avatar's overall
 *     diagonal so the threshold is scale-independent. This is the primary
 *     detach detector and — critically — it does NOT false-positive on the
 *     legitimate lean itself, because the lean is (mostly) a rigid rotation.
 *
 *     IMPORTANT: we use the TRUE vertex centroid (running mean), NOT the
 *     AABB center. The AABB center of an elongated mesh (torso, hair) slides
 *     relative to the geometry as the mesh rotates, so an AABB-center seam
 *     metric reads a clean lean as a 40–90% "tear" — the exact false-positive
 *     that wrongly condemned cleanly-merged rigs (Naomi, Kiera) in an earlier
 *     pass. The mean is rotation-invariant; the AABB center is not.
 *
 *  2. Per-mesh INTRINSIC-radius growth ("spike / explosion").
 *     A broken bind pose or a bad bone-inverse swap makes a mesh's
 *     vertices fly out to huge coordinates (the classic Three.js skinning
 *     "spike explosion"). We measure each mesh's radius as max |vertex −
 *     ownCentroid|, which is rotation+translation invariant (a rigid lean
 *     leaves it at ~1.0×), and flag any mesh whose radius balloons past the
 *     ratio threshold. AABB diagonal is intentionally NOT used — a tilted
 *     box is legitimately bigger, which again confounds rotation with tear.
 *
 * Both samples are taken from the SHIPPED avatar's live SkinnedMeshes via
 * `SkinnedMesh.applyBoneTransform` — the same CPU skinning Three.js uses
 * for raycasting/bounds — so the measurement reflects exactly what the
 * GPU renders. No bespoke render path, no test-only branch in the avatar.
 *
 * CALIBRATION (Jul 28 2026 — probe v2)
 * ------------------------------------
 * v1 (Jun 13 2026) shipped with a sampler bug: it called
 * `applyBoneTransform(i, tmp)` WITHOUT first seeding `tmp` from the position
 * attribute. That method takes the bind-pose vertex *in* `target` and
 * overwrites it — it does not read the geometry itself. So v1 fed each
 * iteration the PREVIOUS iteration's result, turning the sample loop into a
 * fixed-point iteration of an affine map instead of a vertex scan. The
 * "vertices" it measured were a function of `mesh.matrixWorld`, not of the
 * mesh. Consequences observed in production:
 *   • the point clouds collapsed toward a fixed point, so inter-mesh min-gap
 *     read ~0 at both instants ⇒ `maxSeamSep` was vacuously ~0.0–0.2% and the
 *     "authoritative detach signal" could not detect anything;
 *   • the intrinsic radii were the spread of that degenerate blob, so
 *     `maxMeshGrowth` returned arbitrary values (12.9× Naomi, 22.5× Ashley
 *     on shrug) and tripped the hard bar on visibly healthy models.
 * The v1 thresholds (`explosionRatio: 5.0`, "clean articulation measures
 * ~3.9×") were fitted to that garbage, so they carried no information.
 *
 * v2 seeds the sample correctly and re-derives the bars from a measured
 * baseline: all 16 registry models × the 6 skeletal gestures, at peak
 * keyframe amplitude and full intensity (96 pairs), plus injected-fault
 * controls. The healthy envelope is:
 *
 *   signal                 healthy p50   healthy p95   healthy max
 *   seam deltaFraction        0.019%        0.548%        1.212%
 *   intrinsic growth          1.013×        1.425×        1.561×
 *
 * and the two signals are empirically INDEPENDENT — neither subsumes the
 * other, which is why both stay in the verdict:
 *   • injected spike (one gesture-driven bone inverse scaled ×10/×100, so
 *     only the vertices weighted to it fly out) ⇒ growth 1.7–144×, but seam
 *     stays inside the healthy band (≤1.89%). Growth is the ONLY detector.
 *   • injected detach (one mesh's skeleton frozen at rest, i.e. the
 *     multi-armature "hair / clothes left behind" failure) ⇒ seam up to
 *     1.73%, growth unchanged at 1.0×. Seam is the ONLY detector.
 * Demoting growth to telemetry, or gating it on seam corroboration, would
 * therefore blind the probe to the entire spike class.
 *
 * KNOWN GAP — the detach detector does not currently work, and no threshold
 * fixes it. Our gestures are small (spine lean peaks at 0.10 rad ≈ 5.7°), so
 * a mesh that ignores the gesture entirely only displaces ~3–5 cm, while a
 * neck/shoulder seam legitimately opens ~2 cm on the outside of the bend.
 * Measured over the same 96 pairs:
 *
 *   metric (÷ avatar diagonal)   healthy max   total-detach max
 *   min-gap growth                  1.212%          1.730%
 *   centroid-pair drift             8.134%         11.301%
 *
 * The distributions overlap in both cases, so `seamFraction` can only be set
 * as a catastrophic-detach backstop (a mesh flying metres away), not as a
 * detector for the "hair / clothes left behind" class the probe was built
 * for. Centroid-pair drift was implemented and measured as the alternative —
 * it is what §1 of this header originally described — and rejected: `wave`
 * legitimately swings a hand mesh's centroid far enough to dominate the
 * healthy tail. Don't re-derive either of those; a working detach detector
 * needs a different shape (e.g. comparing each mesh's rigid displacement
 * against the bone chain that is supposed to drive it, or restricting the
 * gap scan to the attachment region), which is a metric change with its own
 * calibration pass and is Keith's call, not a threshold tweak.
 *
 * Net: as of v2 the intrinsic-radius signal is the probe's only working
 * detector. Weigh that before quoting a green verdict as evidence that a
 * model's gestures are safe.
 *
 * Re-derive all of the above with `scripts/probe-calibration/` (see its
 * README) before touching a threshold; `skinningIntegrity.test.ts` pins the
 * sampler contract and both failure classes.
 */

import * as THREE from 'three';

/** Bounds of a single skinned mesh in WORLD space at one instant. */
export interface MeshBoundsSample {
  name: string;
  /**
   * TRUE world-space vertex centroid (mean of the sampled skinned verts).
   * This — NOT the AABB center — is what the seam metric compares, because
   * the mean is provably preserved by a rigid rotation+translation (the AABB
   * center is NOT: rotating an elongated mesh slides its axis-aligned box
   * center relative to the geometry, which is exactly the confound that made
   * the old metric false-positive "tear" on a clean lean).
   */
  centroid: [number, number, number];
  /**
   * Intrinsic radius: max distance of any sampled vertex from this mesh's
   * OWN centroid. Rotation+translation invariant — a rigid move leaves it
   * unchanged, so it only grows when the mesh genuinely deforms / explodes
   * (the Three.js skinning "spike"). This is the explosion signal.
   */
  radius: number;
  /** World-space AABB center (kept for display/debug only — NOT the verdict). */
  center: [number, number, number];
  /** World-space AABB size (max − min) — display/debug only. */
  size: [number, number, number];
  /** AABB diagonal length — display/debug only. */
  diagonal: number;
  /**
   * Flat [x,y,z, x,y,z, …] of the sampled skinned world-space vertices.
   * Used to compute the minimum inter-mesh GAP — the only metric that can
   * tell an articulating-but-stitched seam (gap stays ~0) from a real
   * detach (gap opens). Transient; not serialised into the report.
   */
  points: number[];
  /** Total vertices in the mesh (not the sampled count). */
  vertexCount: number;
}

/** One whole-avatar snapshot: every visible skinned mesh + the union AABB. */
export interface AvatarBoundsSnapshot {
  meshes: MeshBoundsSample[];
  /** Union-AABB diagonal across all skinned meshes — the scale normaliser. */
  avatarDiagonal: number;
}

export interface SeamSeparation {
  meshA: string;
  meshB: string;
  /** Minimum vertex-to-vertex gap between the two meshes at rest (≈0 ⇒ touching). */
  restDistance: number;
  /** Minimum vertex-to-vertex gap between the two meshes at gesture peak. */
  peakDistance: number;
  /**
   * How far an in-contact seam pulled APART, as a fraction of the avatar
   * diagonal: max(0, peakGap − restGap) / avatarDiagonal. ~0 ⇒ the seam
   * stayed stitched (articulation is fine); large ⇒ a detach opened a gap.
   */
  deltaFraction: number;
}

export interface MeshGrowth {
  name: string;
  /** peakRadius / restRadius (intrinsic radius). ~1 = no ballooning. */
  growthRatio: number;
}

/**
 * Measurement-contract version carried on every report.
 *
 * 1 = the Jun 13 2026 probe, whose sampler never seeded the bind-pose vertex
 *     and therefore reported values unrelated to the geometry. Any stored
 *     v1 report is noise and must not be used to justify a gesture policy.
 * 2 = Jul 28 2026: correct vertex seeding + thresholds re-derived from a
 *     96-pair healthy baseline and injected-fault controls.
 *
 * Additive field on the public `avatar:gesture:integrity` event — consumers
 * that predate it simply ignore it.
 */
export const SKINNING_INTEGRITY_PROBE_VERSION = 2;

export interface SkinningIntegrityReport {
  gesture: string;
  modelName?: string;
  /** See {@link SKINNING_INTEGRITY_PROBE_VERSION}. */
  probeVersion: number;
  /** true ⇒ meshes moved rigidly together within thresholds. */
  ok: boolean;
  /** Largest inter-mesh seam separation observed (the detach signal). */
  worstSeam?: SeamSeparation;
  /** Largest single-mesh intrinsic-radius growth observed (the spike signal). */
  worstGrowth?: MeshGrowth;
  seams: SeamSeparation[];
  growths: MeshGrowth[];
  thresholds: { seamFraction: number; growthRatio: number; explosionRatio: number };
}

export interface IntegrityThresholds {
  /**
   * Max allowed inter-mesh min-gap growth as a fraction of the avatar
   * diagonal, before the verdict fails.
   *
   * 0.02 (2%) sits 1.65× above the worst healthy reading in the v2 baseline
   * (1.212%, Harper/nod) and below the worst injected total-detach reading
   * (1.73%). Read the KNOWN GAP note in the module header before touching
   * this: because the gap is normalised by the whole-avatar diagonal, the
   * healthy and fully-detached distributions overlap, so 2% is a
   * catastrophic-detach backstop rather than a sensitive detector. Raising
   * it back toward the v1 value of 0.06 makes it unreachable — nothing we
   * can inject, including freezing a whole mesh's skeleton, gets near 6%.
   */
  seamFraction?: number;
  /**
   * REPORTING-ONLY soft threshold for per-mesh INTRINSIC-radius growth.
   * Exceeding it paints the report amber for attention; it does NOT by
   * itself fail the verdict.
   *
   * 1.75 sits just above the worst healthy reading (1.561×, Ashley/wave —
   * a big arm motion genuinely swings the hand mesh's far verts out from its
   * own centroid). The v1 value of 1.25 was below the healthy envelope, so
   * amber fired constantly on clean models and meant nothing.
   */
  growthRatio?: number;
  /**
   * HARD growth threshold — a genuine skinning explosion (vertices flung out
   * by a broken bone weight / a bone inverse from the wrong rest pose).
   *
   * 2.5 is a TIGHTENING from v1's 5.0, which is only safe because v2 measures
   * real vertices: torso-driving gestures top out at 1.09× healthy and the
   * whole healthy population tops out at 1.561×, so 2.5 keeps a 1.6× margin
   * while catching most of an injected ×10 bone-inverse spike (1.7–13.7×) and
   * all of an injected ×100 one (17–144×). Do NOT raise this to make a noisy
   * model pass — if a model reads above 2.5, inspect the GLB (see the
   * workspace rule on diagnosing asset-level bugs from the binary rather than
   * from runtime logs).
   */
  explosionRatio?: number;
  /** Vertex sample cap per mesh (perf). */
  maxSamplesPerMesh?: number;
  /** Ignore meshes smaller than this fraction of the avatar diagonal. */
  minMeshDiagonalFraction?: number;
  /**
   * Two meshes count as "in contact" (an actual seam to watch) when their
   * minimum vertex gap at rest is below this fraction of the avatar diagonal.
   * Non-touching pairs (feet↔head) are excluded — their gap legitimately
   * changes under articulation and tells us nothing about tearing.
   */
  contactFraction?: number;
}

const DEFAULTS: Required<IntegrityThresholds> = {
  seamFraction: 0.02,
  growthRatio: 1.75,
  explosionRatio: 2.5,
  maxSamplesPerMesh: 400,
  minMeshDiagonalFraction: 0.05,
  contactFraction: 0.04,
};

/**
 * Sample the world-space skinned bounds of every visible SkinnedMesh in
 * the avatar scene. Uses `applyBoneTransform` (the engine's own CPU
 * skinning) so the bounds reflect the rendered pose.
 */
export function sampleAvatarBounds(
  scene: THREE.Object3D,
  maxSamplesPerMesh = DEFAULTS.maxSamplesPerMesh,
): AvatarBoundsSnapshot {
  const meshes: MeshBoundsSample[] = [];
  const tmp = new THREE.Vector3();
  const avatarMin = new THREE.Vector3(Infinity, Infinity, Infinity);
  const avatarMax = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

  // Ensure world matrices + skeleton bone matrices reflect the current pose.
  scene.updateMatrixWorld(true);

  scene.traverse((node: THREE.Object3D) => {
    const mesh = node as THREE.SkinnedMesh;
    if (!(mesh as unknown as { isSkinnedMesh?: boolean }).isSkinnedMesh) return;
    if (!mesh.visible) return;
    const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
    const position = geometry?.attributes?.position as
      | THREE.BufferAttribute
      | undefined;
    const skeleton = mesh.skeleton;
    if (!position || !skeleton || position.count === 0) return;

    // `applyBoneTransform(index, target)` / the legacy `boneTransform` take
    // the vertex's BIND-POSE position *in* `target` and overwrite it with the
    // skinned result — they do NOT read the position attribute themselves
    // (that's `Mesh.getVertexPosition`'s job, which SkinnedMesh calls first).
    // So `target` MUST be seeded from the position attribute on every
    // iteration; see the seeding line in the sample loop below.
    const hasApply = typeof (mesh as unknown as {
      applyBoneTransform?: unknown;
    }).applyBoneTransform === 'function';
    const hasLegacy = typeof (mesh as unknown as {
      boneTransform?: unknown;
    }).boneTransform === 'function';
    if (!hasApply && !hasLegacy) return;

    const count = position.count;
    const step = Math.max(1, Math.floor(count / maxSamplesPerMesh));
    const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    // Accumulate the sampled world-space points so we can compute the TRUE
    // centroid (mean) and the intrinsic radius (max |v − centroid|). Both are
    // rotation+translation invariant, unlike the AABB center/diagonal.
    const pts: number[] = [];
    const sum = new THREE.Vector3();
    let sampled = 0;

    for (let i = 0; i < count; i += step) {
      // Seed with the bind-pose vertex. Morph targets are deliberately NOT
      // folded in (we want to measure skinning, not the speech visemes that
      // happen to be mid-flight between the rest and peak snapshots).
      tmp.fromBufferAttribute(position, i);
      if (hasApply) {
        (mesh as unknown as {
          applyBoneTransform: (index: number, target: THREE.Vector3) => THREE.Vector3;
        }).applyBoneTransform(i, tmp);
      } else {
        (mesh as unknown as {
          boneTransform: (index: number, target: THREE.Vector3) => THREE.Vector3;
        }).boneTransform(i, tmp);
      }
      tmp.applyMatrix4(mesh.matrixWorld);
      if (!Number.isFinite(tmp.x) || !Number.isFinite(tmp.y) || !Number.isFinite(tmp.z)) {
        continue;
      }
      min.min(tmp);
      max.max(tmp);
      avatarMin.min(tmp);
      avatarMax.max(tmp);
      pts.push(tmp.x, tmp.y, tmp.z);
      sum.add(tmp);
      sampled++;
    }
    if (sampled === 0) return;

    const centroid = sum.multiplyScalar(1 / sampled);
    // Intrinsic radius: max distance of any sampled vertex from the centroid.
    let radiusSq = 0;
    for (let k = 0; k < pts.length; k += 3) {
      const dx = pts[k] - centroid.x;
      const dy = pts[k + 1] - centroid.y;
      const dz = pts[k + 2] - centroid.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > radiusSq) radiusSq = d2;
    }
    const center = new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5);
    const size = new THREE.Vector3().subVectors(max, min);
    meshes.push({
      name: mesh.name || '(unnamed)',
      centroid: [centroid.x, centroid.y, centroid.z],
      radius: Math.sqrt(radiusSq),
      center: [center.x, center.y, center.z],
      size: [size.x, size.y, size.z],
      diagonal: size.length(),
      points: pts,
      vertexCount: count,
    });
  });

  const avatarDiagonal = meshes.length
    ? new THREE.Vector3().subVectors(avatarMax, avatarMin).length()
    : 0;
  return { meshes, avatarDiagonal };
}

function dist(a: [number, number, number], b: [number, number, number]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Minimum vertex-to-vertex distance between two flat [x,y,z,…] point sets.
 * O(|A|·|B|) — fine because it runs once per gesture on ≤400-pt samples.
 * Squared-distance compare avoids the per-pair sqrt; sqrt once at the end.
 */
function minGap(a: number[], b: number[]): number {
  let best = Infinity;
  for (let i = 0; i < a.length; i += 3) {
    const ax = a[i];
    const ay = a[i + 1];
    const az = a[i + 2];
    for (let j = 0; j < b.length; j += 3) {
      const dx = ax - b[j];
      const dy = ay - b[j + 1];
      const dz = az - b[j + 2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < best) best = d2;
    }
  }
  return best === Infinity ? Infinity : Math.sqrt(best);
}

/**
 * Compare a rest snapshot to a gesture-peak snapshot and decide whether
 * the avatar's skinned meshes moved rigidly together (ok) or tore apart.
 */
export function compareBounds(
  rest: AvatarBoundsSnapshot,
  peak: AvatarBoundsSnapshot,
  gesture: string,
  modelName?: string,
  thresholds?: IntegrityThresholds,
): SkinningIntegrityReport {
  const cfg = { ...DEFAULTS, ...thresholds };
  const denom = rest.avatarDiagonal || peak.avatarDiagonal || 1;
  const peakByName = new Map(peak.meshes.map((m) => [m.name, m]));

  // Only consider meshes present at BOTH instants and large enough to be
  // structural (skip eyelashes / tiny accessories whose centroids are noisy).
  const minDiag = denom * cfg.minMeshDiagonalFraction;
  const common = rest.meshes
    .filter((m) => peakByName.has(m.name) && m.diagonal >= minDiag)
    .sort((a, b) => b.diagonal - a.diagonal);

  const seams: SeamSeparation[] = [];
  // Pairwise among the largest ~8 structural meshes — enough to catch
  // head↔torso / torso↔jacket detaches without O(n²) blowup on rigs with
  // dozens of submeshes.
  const structural = common.slice(0, 8);
  const contactGap = denom * cfg.contactFraction;
  for (let i = 0; i < structural.length; i++) {
    for (let j = i + 1; j < structural.length; j++) {
      const a = structural[i];
      const b = structural[j];
      const pa = peakByName.get(a.name)!;
      const pb = peakByName.get(b.name)!;
      // Minimum vertex GAP — robust to articulation. A seam that stays
      // stitched keeps its rest gap ~0 even while the joint bends.
      const restDistance = minGap(a.points, b.points);
      // Only meshes actually touching at rest form a seam worth watching;
      // a lean legitimately changes the head↔feet gap and that's not a tear.
      if (restDistance > contactGap) continue;
      const peakDistance = minGap(pa.points, pb.points);
      seams.push({
        meshA: a.name,
        meshB: b.name,
        restDistance,
        peakDistance,
        // How far the touching seam pulled apart (clamped at 0 so a seam
        // that tightens doesn't read as separation).
        deltaFraction: Math.max(0, peakDistance - restDistance) / denom,
      });
    }
  }

  const growths: MeshGrowth[] = common.map((m) => {
    const p = peakByName.get(m.name)!;
    // Intrinsic-radius ratio (rotation-invariant) — only a real explosion
    // grows it. AABB diagonal is intentionally NOT used here.
    return {
      name: m.name,
      growthRatio: m.radius > 1e-6 ? p.radius / m.radius : 1,
    };
  });

  const worstSeam = [...seams].sort((a, b) => b.deltaFraction - a.deltaFraction)[0];
  const worstGrowth = [...growths].sort((a, b) => b.growthRatio - a.growthRatio)[0];

  // Two INDEPENDENT detectors, OR-ed. Neither is a corroborating signal for
  // the other — the v2 calibration showed each is blind to the other's
  // failure class (a partial spike leaves the min-gap inside the healthy
  // band; a frozen-skeleton detach leaves the intrinsic radius at 1.0×), so
  // requiring agreement would silently halve the probe's coverage.
  //   • seam (min-gap growth)  ⇒ detach / "left behind" class
  //   • intrinsic-radius ratio ⇒ spike / explosion class
  // Growth between growthRatio and explosionRatio is amber (attention) but
  // still OK: above the healthy envelope, below a real explosion.
  const ok =
    (!worstSeam || worstSeam.deltaFraction <= cfg.seamFraction) &&
    (!worstGrowth || worstGrowth.growthRatio <= cfg.explosionRatio);

  return {
    gesture,
    modelName,
    probeVersion: SKINNING_INTEGRITY_PROBE_VERSION,
    ok,
    worstSeam,
    worstGrowth,
    seams,
    growths,
    thresholds: {
      seamFraction: cfg.seamFraction,
      growthRatio: cfg.growthRatio,
      explosionRatio: cfg.explosionRatio,
    },
  };
}

/** Window event name carrying a {@link SkinningIntegrityReport}. */
export const AVATAR_GESTURE_INTEGRITY_EVENT = 'avatar:gesture:integrity';

/**
 * One-shot integrity probe for a gesture: snapshot rest bounds now, then
 * snapshot again at `peakDelayMs` (near the gesture's hold), compare, emit
 * a `🦴 … integrity` debug log + an `avatar:gesture:integrity` window
 * event. Best-effort: any error is swallowed so a probe failure can never
 * break a gesture.
 */
export function probeGestureIntegrity(
  scene: THREE.Object3D,
  gesture: string,
  peakDelayMs: number,
  modelName?: string,
  thresholds?: IntegrityThresholds,
): void {
  try {
    const rest = sampleAvatarBounds(scene, thresholds?.maxSamplesPerMesh);
    if (rest.meshes.length === 0) return;
    const delay = Math.max(120, Math.min(peakDelayMs, 4000));
    window.setTimeout(() => {
      try {
        const peak = sampleAvatarBounds(scene, thresholds?.maxSamplesPerMesh);
        const report = compareBounds(rest, peak, gesture, modelName, thresholds);
        const seamPct = report.worstSeam
          ? (report.worstSeam.deltaFraction * 100).toFixed(1)
          : '0.0';
        const grow = report.worstGrowth
          ? report.worstGrowth.growthRatio.toFixed(2)
          : '1.00';
        // Amber: seam is clean (no detach) but a mesh's intrinsic radius grew
        // past the soft bar — almost always benign articulation, surfaced for
        // attention only. TEAR is reserved for a genuine detach (seam) or a
        // real explosion (growth past the hard bar).
        const growthAmber =
          report.ok &&
          !!report.worstGrowth &&
          report.worstGrowth.growthRatio > report.thresholds.growthRatio;
        const verdict = report.ok ? (growthAmber ? 'OK·growth' : 'OK') : 'TEAR';
        const seamPair = report.worstSeam
          ? `${report.worstSeam.meshA}↔${report.worstSeam.meshB}`
          : 'n/a';
        // eslint-disable-next-line no-console
        console.debug(
          `🦴 Gesture '${gesture}' — integrity ${verdict}: maxSeamSep=${seamPct}% (${seamPair}), maxMeshGrowth=${grow}x (${report.worstGrowth?.name ?? 'n/a'}) on ${modelName ?? 'unknown'}`,
        );
        window.dispatchEvent(
          new CustomEvent(AVATAR_GESTURE_INTEGRITY_EVENT, { detail: report }),
        );
      } catch {
        /* never break a gesture on a probe failure */
      }
    }, delay);
  } catch {
    /* never break a gesture on a probe failure */
  }
}
